import { useState, useEffect, useCallback } from 'react'
import { api } from '@api/client'
import { appsyncQuery } from '@api/appsync-client'

const LIST_ADMIN_USERS = `
  query ListAdminUsers($search: String, $status: String, $team: String, $page: Int, $limit: Int) {
    listAdminUsers(search: $search, status: $status, team: $team, page: $page, limit: $limit) {
      users {
        userId email name managerId managerName team status joinedAt
        totalSessions completedSessions lastActiveAt
      }
      total page limit
    }
  }
`

export function useAdminUsers({ search = '', status = 'all', team = 'all', page = 1 } = {}) {
  const [users, setUsers] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(LIST_ADMIN_USERS, { search, status, team, page })
      const result = data.listAdminUsers
      setUsers(result.users || [])
      setTotal(result.total || 0)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [search, status, team, page])

  useEffect(() => { load() }, [load])

  const inviteUser = useCallback(async (body) => {
    await api.adminInviteUser(body)
    load()
  }, [load])

  const bulkInvite = useCallback(async (csv) => {
    const result = await api.adminBulkInvite(csv)
    load()
    return result
  }, [load])

  const deactivateUser = useCallback(async (userId) => {
    await api.adminDeactivateUser(userId)
    load()
  }, [load])

  const reactivateUser = useCallback(async (userId) => {
    await api.adminReactivateUser(userId)
    load()
  }, [load])

  const resendInvite = useCallback(async (userId) => {
    await api.adminResendInvite(userId)
    load()
  }, [load])

  const updateUser = useCallback(async (userId, body) => {
    await api.adminUpdateUser(userId, body)
    load()
  }, [load])

  return { users, total, loading, error, inviteUser, bulkInvite, deactivateUser, reactivateUser, resendInvite, updateUser, reload: load }
}
